import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval, parseISO, isWithinInterval } from 'date-fns';
import { 
  TrendingUp, TrendingDown, Clock, DollarSign, Users, AlertTriangle,
  ChevronLeft, ChevronRight, Calendar, Target, Activity, Timer,
  CheckCircle, AlertCircle, BarChart3, PieChart, ArrowUp, ArrowDown,
  Minus, Filter, Download, RefreshCw
} from 'lucide-react';
import './LaborAnalysis.css';

function LaborAnalysis() {
  const { state } = useApp();
  const [dateRange, setDateRange] = useState('today'); // today, week, month, custom
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [viewMode, setViewMode] = useState('overview'); // overview, byTeam, byJob
  const [showFilters, setShowFilters] = useState(false);

  // Get date range bounds
  const getDateBounds = () => {
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return { start: today, end: today };
      case 'week':
        return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) };
      case 'month':
        return { start: subDays(today, 30), end: today };
      case 'custom':
        return { start: parseISO(selectedDate), end: parseISO(selectedDate) };
      default:
        return { start: today, end: today };
    }
  };

  const dateBounds = getDateBounds();

  // Filter jobs within date range
  const filteredJobs = useMemo(() => {
    return state.jobs.filter(job => {
      const jobDate = parseISO(job.date);
      return isWithinInterval(jobDate, { start: dateBounds.start, end: dateBounds.end });
    });
  }, [state.jobs, dateBounds]);

  // Calculate labor metrics for a single job
  const calculateJobMetrics = (job) => {
    const expectedMinutes = (job.duration || 0) * 60;
    const actualMinutes = job.actual_duration_min || 0;
    const varianceMinutes = actualMinutes - expectedMinutes;
    const variancePercent = expectedMinutes > 0 ? (varianceMinutes / expectedMinutes) * 100 : 0;
    
    // Get team members and their pay rates
    const teamMembers = (job.team || []).map(empId => 
      state.employees.find(e => e.id === empId)
    ).filter(Boolean);
    
    // Calculate average hourly rate for the team
    const avgHourlyRate = teamMembers.length > 0
      ? teamMembers.reduce((sum, emp) => sum + (emp.hourlyRate || 18), 0) / teamMembers.length
      : 18; // Default rate
    
    // Labor cost calculation
    const actualHours = actualMinutes / 60;
    const laborCost = actualHours * avgHourlyRate * teamMembers.length;
    
    // Labor percentage of job revenue
    const laborPercent = job.price > 0 ? (laborCost / job.price) * 100 : 0;
    
    // Profit
    const profit = job.price - laborCost;
    const profitMargin = job.price > 0 ? (profit / job.price) * 100 : 0;
    
    // Status indicators
    let status = 'on_track';
    if (variancePercent > 20) status = 'over';
    else if (variancePercent > 10) status = 'warning';
    else if (variancePercent < -10) status = 'under';
    
    return {
      ...job,
      expectedMinutes,
      actualMinutes,
      varianceMinutes,
      variancePercent,
      avgHourlyRate,
      laborCost,
      laborPercent,
      profit,
      profitMargin,
      teamCount: teamMembers.length,
      status
    };
  };

  // Calculate metrics for all filtered jobs
  const jobMetrics = useMemo(() => {
    return filteredJobs.map(calculateJobMetrics);
  }, [filteredJobs, state.employees]);

  // Filter to only completed jobs for actual metrics
  const completedJobs = useMemo(() => {
    return jobMetrics.filter(j => j.status === 'completed' || j.actual_duration_min > 0);
  }, [jobMetrics]);

  // Aggregate statistics
  const aggregateStats = useMemo(() => {
    const completed = completedJobs;
    
    if (completed.length === 0) {
      return {
        totalJobs: filteredJobs.length,
        completedJobs: 0,
        totalRevenue: filteredJobs.reduce((sum, j) => sum + (j.price || 0), 0),
        totalExpectedHours: filteredJobs.reduce((sum, j) => sum + (j.duration || 0), 0),
        totalActualHours: 0,
        totalLaborCost: 0,
        avgLaborPercent: 0,
        avgProfitMargin: 0,
        overTimeJobs: 0,
        onTimeJobs: 0,
        underTimeJobs: 0,
        hasData: false
      };
    }

    const totalRevenue = completed.reduce((sum, j) => sum + (j.price || 0), 0);
    const totalExpectedMinutes = completed.reduce((sum, j) => sum + j.expectedMinutes, 0);
    const totalActualMinutes = completed.reduce((sum, j) => sum + j.actualMinutes, 0);
    const totalLaborCost = completed.reduce((sum, j) => sum + j.laborCost, 0);
    
    const avgLaborPercent = totalRevenue > 0 ? (totalLaborCost / totalRevenue) * 100 : 0;
    const avgProfitMargin = totalRevenue > 0 ? ((totalRevenue - totalLaborCost) / totalRevenue) * 100 : 0;
    
    const overTimeJobs = completed.filter(j => j.variancePercent > 10).length;
    const onTimeJobs = completed.filter(j => j.variancePercent >= -10 && j.variancePercent <= 10).length;
    const underTimeJobs = completed.filter(j => j.variancePercent < -10).length;

    return {
      totalJobs: filteredJobs.length,
      completedJobs: completed.length,
      totalRevenue,
      totalExpectedHours: totalExpectedMinutes / 60,
      totalActualHours: totalActualMinutes / 60,
      totalLaborCost,
      avgLaborPercent,
      avgProfitMargin,
      overTimeJobs,
      onTimeJobs,
      underTimeJobs,
      varianceHours: (totalActualMinutes - totalExpectedMinutes) / 60,
      hasData: true
    };
  }, [completedJobs, filteredJobs]);

  // Group by team member
  const teamStats = useMemo(() => {
    const byEmployee = {};
    
    completedJobs.forEach(job => {
      (job.team || []).forEach(empId => {
        if (!byEmployee[empId]) {
          const emp = state.employees.find(e => e.id === empId);
          byEmployee[empId] = {
            employeeId: empId,
            name: emp ? `${emp.firstName} ${emp.lastName || ''}`.trim() : 'Unknown',
            hourlyRate: emp?.hourlyRate || 18,
            jobs: [],
            totalMinutes: 0,
            totalRevenue: 0,
            totalLaborCost: 0
          };
        }
        
        // Split job metrics by team size
        const share = 1 / (job.team?.length || 1);
        byEmployee[empId].jobs.push(job);
        byEmployee[empId].totalMinutes += job.actualMinutes * share;
        byEmployee[empId].totalRevenue += (job.price || 0) * share;
      });
    });

    // Calculate labor cost and metrics for each employee
    return Object.values(byEmployee).map(emp => {
      const hours = emp.totalMinutes / 60;
      const laborCost = hours * emp.hourlyRate;
      const laborPercent = emp.totalRevenue > 0 ? (laborCost / emp.totalRevenue) * 100 : 0;
      const sqftPerHour = hours > 0 
        ? emp.jobs.reduce((sum, j) => {
            const prop = state.properties.find(p => p.id === j.propertyId);
            return sum + ((prop?.sqft || 1500) / (j.team?.length || 1));
          }, 0) / hours
        : 0;
      
      return {
        ...emp,
        hours,
        laborCost,
        laborPercent,
        sqftPerHour: Math.round(sqftPerHour),
        jobCount: emp.jobs.length
      };
    }).sort((a, b) => b.hours - a.hours);
  }, [completedJobs, state.employees, state.properties]);

  // Jobs over time (for alerts)
  const alertJobs = useMemo(() => {
    return jobMetrics
      .filter(j => j.status === 'over' || (j.status === 'in_progress' && j.varianceMinutes > 15))
      .sort((a, b) => b.varianceMinutes - a.varianceMinutes);
  }, [jobMetrics]);

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '';
    if (hours === 0) return `${sign}${mins}m`;
    return mins > 0 ? `${sign}${hours}h ${mins}m` : `${sign}${hours}h`;
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getVarianceColor = (percent) => {
    if (percent > 20) return 'var(--red)';
    if (percent > 10) return 'var(--orange)';
    if (percent < -10) return 'var(--sage)';
    return 'var(--text-dark)';
  };

  const getLaborPercentColor = (percent) => {
    if (percent <= 35) return 'var(--sage)';
    if (percent <= 40) return 'var(--gold)';
    if (percent <= 50) return 'var(--orange)';
    return 'var(--red)';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'over': return <AlertTriangle size={16} />;
      case 'warning': return <AlertCircle size={16} />;
      case 'under': return <TrendingDown size={16} />;
      default: return <CheckCircle size={16} />;
    }
  };

  // Date navigation
  const navigateDate = (direction) => {
    const current = parseISO(selectedDate);
    const newDate = direction === 'prev' ? subDays(current, 1) : subDays(current, -1);
    setSelectedDate(format(newDate, 'yyyy-MM-dd'));
    setDateRange('custom');
  };

  return (
    <div className="labor-analysis">
      {/* Header */}
      <div className="labor-header">
        <div className="header-title">
          <h1>Labor Analysis</h1>
          <p className="subtitle">Track labor costs and job efficiency</p>
        </div>
        
        <div className="header-controls">
          {/* Date Range Selector */}
          <div className="date-range-selector">
            <button 
              className={`range-btn ${dateRange === 'today' ? 'active' : ''}`}
              onClick={() => setDateRange('today')}
            >
              Today
            </button>
            <button 
              className={`range-btn ${dateRange === 'week' ? 'active' : ''}`}
              onClick={() => setDateRange('week')}
            >
              This Week
            </button>
            <button 
              className={`range-btn ${dateRange === 'month' ? 'active' : ''}`}
              onClick={() => setDateRange('month')}
            >
              Last 30 Days
            </button>
          </div>
          
          {/* Date Navigator */}
          <div className="date-navigator">
            <button className="nav-btn" onClick={() => navigateDate('prev')}>
              <ChevronLeft size={20} />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setDateRange('custom');
              }}
              className="date-input"
            />
            <button className="nav-btn" onClick={() => navigateDate('next')}>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Target Banner */}
      <div className="target-banner">
        <div className="target-icon">
          <Target size={24} />
        </div>
        <div className="target-content">
          <span className="target-label">Labor Cost Target</span>
          <span className="target-value">40%</span>
        </div>
        <div className="target-current">
          <span className="current-label">Current</span>
          <span 
            className="current-value"
            style={{ color: getLaborPercentColor(aggregateStats.avgLaborPercent) }}
          >
            {aggregateStats.hasData ? `${aggregateStats.avgLaborPercent.toFixed(1)}%` : '--'}
          </span>
        </div>
        <div className="target-gap">
          {aggregateStats.hasData && aggregateStats.avgLaborPercent > 40 && (
            <>
              <ArrowDown size={16} className="gap-icon red" />
              <span className="gap-text">
                {(aggregateStats.avgLaborPercent - 40).toFixed(1)}% over target
              </span>
            </>
          )}
          {aggregateStats.hasData && aggregateStats.avgLaborPercent <= 40 && (
            <>
              <CheckCircle size={16} className="gap-icon green" />
              <span className="gap-text green">On target!</span>
            </>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Expected Hours</span>
            <span className="stat-value">{aggregateStats.totalExpectedHours.toFixed(1)}h</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <Timer size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Actual Hours</span>
            <span className="stat-value">
              {aggregateStats.hasData ? `${aggregateStats.totalActualHours.toFixed(1)}h` : '--'}
            </span>
            {aggregateStats.hasData && (
              <span 
                className="stat-variance"
                style={{ color: getVarianceColor((aggregateStats.varianceHours / aggregateStats.totalExpectedHours) * 100) }}
              >
                {aggregateStats.varianceHours > 0 ? '+' : ''}{aggregateStats.varianceHours.toFixed(1)}h
              </span>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Revenue</span>
            <span className="stat-value">{formatCurrency(aggregateStats.totalRevenue)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Labor Cost</span>
            <span className="stat-value">
              {aggregateStats.hasData ? formatCurrency(aggregateStats.totalLaborCost) : '--'}
            </span>
            <span 
              className="stat-percent"
              style={{ color: getLaborPercentColor(aggregateStats.avgLaborPercent) }}
            >
              {aggregateStats.hasData ? `${aggregateStats.avgLaborPercent.toFixed(1)}%` : '--'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="analysis-grid">
        {/* Jobs Performance */}
        <div className="analysis-card jobs-performance">
          <div className="card-header">
            <h2>
              <Activity size={20} />
              Job Time Performance
            </h2>
            <div className="performance-summary">
              <span className="perf-item green">
                <CheckCircle size={14} /> {aggregateStats.onTimeJobs} on time
              </span>
              <span className="perf-item orange">
                <AlertCircle size={14} /> {aggregateStats.overTimeJobs} over
              </span>
            </div>
          </div>
          
          <div className="jobs-list">
            {jobMetrics.length === 0 ? (
              <div className="empty-state">
                <Calendar size={48} />
                <p>No jobs found for selected date range</p>
              </div>
            ) : (
              jobMetrics.map(job => (
                <div key={job.id} className={`job-row ${job.status}`}>
                  <div className="job-info">
                    <span className="job-client">{job.clientName}</span>
                    <span className="job-address">{job.address}</span>
                  </div>
                  
                  <div className="job-time">
                    <div className="time-expected">
                      <span className="time-label">Expected</span>
                      <span className="time-value">{formatTime(job.expectedMinutes)}</span>
                    </div>
                    <div className="time-actual">
                      <span className="time-label">Actual</span>
                      <span className="time-value">
                        {job.actualMinutes > 0 ? formatTime(job.actualMinutes) : '--'}
                      </span>
                    </div>
                    <div className="time-variance" style={{ color: getVarianceColor(job.variancePercent) }}>
                      {job.actualMinutes > 0 ? (
                        <>
                          {getStatusIcon(job.status)}
                          <span>{formatPercent(job.variancePercent)}</span>
                        </>
                      ) : (
                        <span className="pending">Pending</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="job-financials">
                    <div className="financial-item">
                      <span className="fin-label">Revenue</span>
                      <span className="fin-value">{formatCurrency(job.price)}</span>
                    </div>
                    <div className="financial-item">
                      <span className="fin-label">Labor</span>
                      <span 
                        className="fin-value"
                        style={{ color: getLaborPercentColor(job.laborPercent) }}
                      >
                        {job.actualMinutes > 0 ? formatCurrency(job.laborCost) : '--'}
                      </span>
                    </div>
                    <div className="financial-item">
                      <span className="fin-label">Labor %</span>
                      <span 
                        className="fin-value"
                        style={{ color: getLaborPercentColor(job.laborPercent) }}
                      >
                        {job.actualMinutes > 0 ? `${job.laborPercent.toFixed(0)}%` : '--'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Team Performance */}
        <div className="analysis-card team-performance">
          <div className="card-header">
            <h2>
              <Users size={20} />
              Team Performance
            </h2>
          </div>
          
          <div className="team-list">
            {teamStats.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No completed jobs to analyze</p>
              </div>
            ) : (
              teamStats.map(member => (
                <div key={member.employeeId} className="team-row">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.name.charAt(0)}
                    </div>
                    <div className="member-details">
                      <span className="member-name">{member.name}</span>
                      <span className="member-jobs">{member.jobCount} jobs</span>
                    </div>
                  </div>
                  
                  <div className="member-metrics">
                    <div className="metric">
                      <span className="metric-label">Hours</span>
                      <span className="metric-value">{member.hours.toFixed(1)}h</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Labor $</span>
                      <span className="metric-value">{formatCurrency(member.laborCost)}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Labor %</span>
                      <span 
                        className="metric-value"
                        style={{ color: getLaborPercentColor(member.laborPercent) }}
                      >
                        {member.laborPercent.toFixed(0)}%
                      </span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Sqft/hr</span>
                      <span className="metric-value">{member.sqftPerHour}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Alerts Panel */}
        {alertJobs.length > 0 && (
          <div className="analysis-card alerts-panel">
            <div className="card-header alert-header">
              <h2>
                <AlertTriangle size={20} />
                Time Alerts
              </h2>
              <span className="alert-count">{alertJobs.length}</span>
            </div>
            
            <div className="alerts-list">
              {alertJobs.map(job => (
                <div key={job.id} className="alert-row">
                  <div className="alert-icon">
                    <AlertTriangle size={18} />
                  </div>
                  <div className="alert-content">
                    <span className="alert-title">{job.clientName}</span>
                    <span className="alert-detail">
                      {formatTime(job.varianceMinutes)} over expected
                      ({formatPercent(job.variancePercent)})
                    </span>
                  </div>
                  <div className="alert-team">
                    {job.teamNames?.join(' & ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats Footer */}
      <div className="quick-stats">
        <div className="quick-stat">
          <span className="qs-label">Jobs Today</span>
          <span className="qs-value">{aggregateStats.totalJobs}</span>
        </div>
        <div className="quick-stat">
          <span className="qs-label">Completed</span>
          <span className="qs-value">{aggregateStats.completedJobs}</span>
        </div>
        <div className="quick-stat">
          <span className="qs-label">Profit Margin</span>
          <span className="qs-value" style={{ color: aggregateStats.avgProfitMargin >= 60 ? 'var(--sage)' : 'var(--orange)' }}>
            {aggregateStats.hasData ? `${aggregateStats.avgProfitMargin.toFixed(0)}%` : '--'}
          </span>
        </div>
        <div className="quick-stat target">
          <span className="qs-label">Target: 40% Labor</span>
          <span className="qs-value">= 60% Profit Margin</span>
        </div>
      </div>
    </div>
  );
}

export default LaborAnalysis;
